export enum PageState {
  EMAIL = 'email',
  PATIENT_INFO = 'patient-information',
  PAYMENT = 'pay',
}

export enum PatientConsentTypes {
  PRACTICE = 'bookAppointmentConsent',
}

export enum AppointmentConsentTypes {
  ENROLLMENT = 'paymentConsent',
}

export enum ConsentFor {
  PATIENT = 'patient',
  APPOINTMENT = 'appointment',
}

export enum AgeGroup {
  AGE_GROUP = 'client_age_group',
}

export enum Gender {
  MALE = 'Male',
  FEMALE = 'Female',
  NON_BINARY = 'Non-binary',
}

// Display label for Non-binary (different from backend value)
export const NON_BINARY_DISPLAY_LABEL = 'Nonbinary / X'

export const unknownGender = 'Unknown'

export enum MinRelationships {
  SELF = 'Self',
  SPOUSE = 'Spouse',
  CHILD = 'Child',
  OTHER = 'Other',
}

/**
 * @shared Used by:
 *   - V2 Booking: components/app/NewBookings/InsuranceSection/* (origin)
 *   - Insurance Management:
 *       - app/insurance-management/utils/buildClientList.ts
 *         (`CHILD_LIKE_RELATIONSHIPS` set + `ELIGIBLE_RELATIONSHIPS` picker
 *         filter)
 *       - app/insurance-management/utils/buildSavePayload.ts (CHILD-flow
 *         "Sarah (me)" identity pass-through assumes these strings are AMD
 *         PatientRelationship descriptions: Child→19, Stepson or
 *         Stepdaughter→17, Grandson or Granddaughter→05, Foster Child→10,
 *         Nephew or Niece→07)
 *
 * The string values must stay stable. Renaming any value will silently
 * break IM's AMD mapping for CHILD-patient flows — see decisions.md
 * 2026-05-04 for context. Coordinate with IM before changes.
 */
export const ChildRelationships = {
  Child: 'Child',
  Grandchild: 'Grandson or Granddaughter',
  Stepchild: 'Stepson or Stepdaughter',
  NephewOrNiece: 'Nephew or Niece',
  FosterChild: 'Foster Child',
}

export const CouplesRelationships = {
  Spouse: 'Spouse',
  SignificantOther: 'Significant other',
  OtherAdult: 'Other Adult',
  LifePartner: 'Life Partner',
  OtherRelationship: 'Other Relationship',
}

export const OtherRelationships = {
  Grandparent: 'Grandfather or Grandmother',
  Grandchild: 'Grandson or Granddaughter',
  Stepchild: 'Stepson or Stepdaughter',
  NephewOrNiece: 'Nephew or Niece',
  FosterChild: 'Foster Child',
  SignificantOther: 'Significant other',
  Mother: 'Mother',
  Father: 'Father',
  OtherAdult: 'Other Adult',
  LifePartner: 'Life Partner',
  OtherRelationship: 'Other Relationship',
}

export const OtherRelationShipsLabel = {
  Grandparent: 'Grandparent',
  Grandchild: 'Grandchild',
  Stepchild: 'Stepchild',
}

export const AllRelationships = {
  Spouse: 'Spouse',
  Child: 'Child',
  ...OtherRelationships,
}

export const appointmentModeValueMapping = {
  Online: 'Video visit',
  'In Person': 'In-person',
}

export enum SlotAvailabilityType {
  ONLINE = 'online',
  INPERSON = 'in_person',
}

export enum CounselingTypeEnum {
  INDIVIDUAL = 'Individual Counseling',
  FAMILY = 'Family Counseling',
  MEDICATION = 'Medication/Psychiatry',
  PSYCHOLOGICAL = 'Psychological Testing & Evaluation',
  COUPLES = 'Couples/Marriage Counseling',
}

export type SalesforcePatientPhoneValidationStatus = {
  phoneNumber: string
  success: boolean
}

export const slotAvailabilityEnumMapping = {
  online: SlotAvailabilityType.ONLINE,
  in_person: SlotAvailabilityType.INPERSON,
}

export const slotAvailabilityLabels = {
  online: 'Video visit',
  in_person: 'In-person',
}

export const paymentCardDetailsInitialValues: PaymentCardDetails = {
  paymentCardNumber: '',
  paymentCardExpiry: '',
  paymentCardCVC: '',
}

export const paymentDetailsFormInitialState: PaymentDetails = {
  firstName: '',
  lastName: '',
  address: {
    line1: '',
    line2: '',
    city: '',
    state: '',
    country: '',
    zipcode: '',
  },
}

export const paymentFormInitialState: SupportConsentValues = {
  appointmentConsents: [
    {
      consentType: AppointmentConsentTypes.ENROLLMENT,
      consentRequestedOn: '',
      consentProvidedOn: '',
      isConsentProvided: false,
      rawResponse: '',
      source: '',
    },
  ],
}

export const authorizationDetailsInitialState: AuthorizationDetails = {
  authorizationFormData: {
    personalInformation: {
      gender: '',
      memberId: '',
    },
    referralInformation: {
      referringProviderEhrId: null,
      referralReason: '',
      effectiveDate: '',
      expirationDate: '',
      insuranceCarrier: null,
    },
    authorizationInformation: {
      authorizationNumber: '',
      authorizationDescription: '',
      hasSessionLimit: true,
      sessionLimit: '',
    },
  },
}

export const NAME_REG = new RegExp(
  "^(?=.*\\p{L})[\\p{L}\\p{M} '\\-.]{1,50}$",
  'u'
)
export const EMAIL_REG = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
export const PHONE_NUMBER_REG = /^(\+\d{1,2}\s?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/
export const CHARACTER_REG = /^[aA-zZ\s]+$/
export const NUMBER_REG = /^[0-9\b]+$/
export const MEMBER_ID_REG = /^[a-zA-Z0-9]*$/
export const NOT_ALPHABET_REG = /[^A-Za-z]/g
export const DECIMAL_NUMBER_REG = /^\d+(\.\d+)?$/
export const SIX_DIGIT_OTP = /^\d{6}$/

export const bookingInsuranceErrors = {
  INSURANCE_DOES_NOT_MATCH_WITH_PATIENT:
    'Selected insurance details should be same as primary patient details',
}

export const addressKeysToCheck = [
  'line1',
  'city',
  'state',
  'country',
  'postal_code',
]

export enum PaymentStatus {
  SUCCEEDED = 'succeeded',
  FAILED = 'failed',
  REFUNDED = 'refunded',
}

export enum AmdPayFormFieldId {
  CARD_NUMBER = 'card-number',
  CARD_EXP = 'card-expiration',
  CARD_CVV = 'card-cvv',
}

export enum AmdPayCardStatus {
  NEW = 'new',
  EXISTING = 'existing',
}

export enum BookingFailureErrorMsg {
  PAGE_SUBMISSION_ERROR = 'Failure occurred while page submission',
  INSURANCE_SUBMISSION_ERROR = 'Failure occurred while insurance submission',
  FETCHING_PATIENT_DATA_ERROR = 'Failure occurred while fetching Patient data',
  FETCHING_PROVIDER_DATA_ERROR = 'Failure occurred while fetching Provider data',
  FETCHING_FILTER_DATA_ERROR = 'Failure occurred while fetching Filter data',
  FETCHING_USER_DATA_ERROR = 'Failure occurred while fetching User data',
  FETCHING_B2B_USER_DATA_ERROR = 'Failure occurred while fetching B2B User data',
  FETCHING_USER_BOOKING_DATA_ERROR = 'Failure occurred while fetching User`s all booking related data',
  CROSS_STATE_BOOKING_ERROR = 'Failure occurred on patient page for cross-state booking',
  PARTNERSHIP_ASSOCIATION_ERROR = 'Failure occurred while associating partnership',
}

export enum BookingErrorUserFacingMsg {
  USER_DATA_ERROR = "We're having trouble connecting to your account info right now.",
  FILTER_DATA_ERROR = "There's a temporary delay loading these search options. Thanks for your patience!",
  PROVIDER_DATA_ERROR = "We're having trouble reaching the provider's system to get their latest details.",
  PARTNERSHIP_ASSOCIATION_ERROR = "We're having trouble verifying your details with our partner right now.",
  PATIENT_DATA_ERROR = "We're having trouble accessing your client records from our external database.",
  UNEXPECTED = "One of our external services is temporarily unavailable. We're working to get things back on track!",
}

export enum BookingButtonLocation {
  TOP_NAVIGATION_BAR = 'Top Navigation bar',
  FOOTER = 'Footer',
  MOBILE_STICKY_CTA_BAR = 'Mobile sticky CTA bar',
  DASHBOARD_UPCOMING_SESSIONS_EMPTY_STATE = 'Dashboard upcoming sessions empty state',
  SESSION_UPCOMING_SESSIONS_EMPTY_STATE = 'Session upcoming sessions empty state',
  SESSION_PAST_SESSIONS_EMPTY_STATE = 'Session past sessions empty state',
  SESSION_CANCELED_SESSIONS_EMPTY_STATE = 'Session canceled sessions empty state',
}

export const EMAIL_CREATE_ACTIONS_LIST = [
  'All responsible parties MUST be 18 years +',
  'Minors MUST have a parent / legal guardian listed as the responsible party',
]

export const EMAIL_CREATE_ACTIONS_LIST_DEFAULT = [
  'This person will be the ThriveWeb account holder',
  ...EMAIL_CREATE_ACTIONS_LIST,
]

export const EMAIL_CREATE_ACTIONS_LIST_SUPPORT = [
  'This person will be the online account holder at Thriveworks.com, where they can manage their appointments',
  ...EMAIL_CREATE_ACTIONS_LIST,
]

export enum PatientGender {
  MALE = 'Male',
  FEMALE = 'Female',
}

export enum ReferralPlanStatus {
  TALK_THERAPY = 'Talk Therapy',
  PSYCHIATRY = 'Psychiatry / Medication Management',
  FAMILY_THERAPY = 'Family Therapy',
  COUPLES_THERAPY = 'Couples Therapy',
}

export const ReferralPlanTherapyTypeAbbreviations = {
  [ReferralPlanStatus.TALK_THERAPY]: 'TT',
  [ReferralPlanStatus.PSYCHIATRY]: 'MM',
  [ReferralPlanStatus.FAMILY_THERAPY]: 'FT',
  [ReferralPlanStatus.COUPLES_THERAPY]: 'CT',
}

export const CounselingTypeToReferralPlanStatus: Record<
  CounselingTypeEnum,
  ReferralPlanStatus
> = {
  [CounselingTypeEnum.INDIVIDUAL]: ReferralPlanStatus.TALK_THERAPY,
  [CounselingTypeEnum.FAMILY]: ReferralPlanStatus.FAMILY_THERAPY,
  [CounselingTypeEnum.MEDICATION]: ReferralPlanStatus.PSYCHIATRY,
  [CounselingTypeEnum.PSYCHOLOGICAL]: ReferralPlanStatus.TALK_THERAPY,
  [CounselingTypeEnum.COUPLES]: ReferralPlanStatus.COUPLES_THERAPY,
}

export const patientGenderOptions: SelectItem[] = [
  {
    label: PatientGender.MALE,
    value: PatientGender.MALE,
  },
  {
    label: PatientGender.FEMALE,
    value: PatientGender.FEMALE,
  },
]

export const referralPlanStatusOptions: SelectItem[] = [
  {
    label: ReferralPlanStatus.TALK_THERAPY,
    value: ReferralPlanStatus.TALK_THERAPY,
  },
  {
    label: ReferralPlanStatus.PSYCHIATRY,
    value: ReferralPlanStatus.PSYCHIATRY,
  },
  {
    label: ReferralPlanStatus.FAMILY_THERAPY,
    value: ReferralPlanStatus.FAMILY_THERAPY,
  },
  {
    label: ReferralPlanStatus.COUPLES_THERAPY,
    value: ReferralPlanStatus.COUPLES_THERAPY,
  },
]

export const bookingErrorMessages = {
  AMD_ERROR_SUPPORT:
    'Unable to book - AMDPay Error. Something went wrong when trying to book appointment. Please try again. If you continue to receive an error, notify your Supervisor.',
  AMD_ERROR_USER:
    'Something went wrong when trying to book appointment. Please try again or call us for help at',
  NO_OPEN_SLOT_USER:
    'The provider is not accepting bookings at the moment. Please try again at a later stage.',
  NO_OPEN_SLOT_SUPPORT:
    'There are no remaining slots for this provider at this time. Please find a different provider or contact your supervisor.',
  SOMETHING_WENT_WRONG:
    'Something went wrong when trying to book appointment. Please try again.',
}

// Stimulant state buckets live in Harness flag STIMULANT_STATE_BUCKET_MESSAGES
// (see hooks/bookings/useStimulantBucket.ts).

export const maxFetchAuthorizationsApiCallCounter = 5
export const delayFetchAuthorizationsApiCall = 2000

export const bookAppointmentButtonId = 'book-appointment-button'
